import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { Bell, Check, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

const POLL_MS = 60000;

export default function NotificationsBell() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications", { params: { limit: 20 } });
        if (cancelled) return;
        setItems(data.notifications || []);
        setUnread(data.unread || 0);
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAuthenticated]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!isAuthenticated) return null;

  const onItemClick = async (n) => {
    try {
      if (!n.read) {
        await api.put(`/notifications/${n.notification_id}/read`);
      }
    } catch {
      /* ignore */
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    // refresh
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.put("/notifications/read-all");
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.notifications || []);
      setUnread(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ease-out active:scale-90 ${
          unread > 0
            ? "bg-[linear-gradient(135deg,#2563EB_0%,#4F8EF7_100%)] text-white shadow-[0_6px_18px_-6px_rgba(37,99,235,0.6)] hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.75)] hover:-translate-y-0.5"
            : "bg-white text-neutral-700 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_6px_16px_-10px_rgba(15,23,42,0.35)] ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5"
        }`}
        data-testid="notifications-bell"
        aria-label={unread > 0 ? `Notificaciones (${unread} sin leer)` : "Notificaciones"}
      >
        <Bell className={`w-[18px] h-[18px] ${unread > 0 ? "animate-[wiggle_1.6s_ease-in-out_infinite]" : ""}`} strokeWidth={2} />
        {unread > 0 && (
          <>
            {/* Pulse halo behind the badge */}
            <span
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 opacity-60 animate-ping"
              aria-hidden
            />
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white shadow-md"
              data-testid="notifications-unread-count"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          </>
        )}
      </button>

      {open && (
        <div
          className="fixed left-2 right-2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-[340px] sm:max-w-[92vw] bg-white border border-neutral-200 shadow-lg rounded-xl z-50 overflow-hidden"
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-neutral-100">
            <p className="text-sm font-semibold">Notificaciones</p>
            {unread > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={markAllRead}
                className="h-7 text-xs"
                disabled={loading}
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                Marcar todo
              </Button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-8">Sin notificaciones</p>
            ) : (
              items.map((n) => (
                <button
                  type="button"
                  key={n.notification_id}
                  onClick={() => onItemClick(n)}
                  className={`w-full text-left px-3 py-2.5 border-b border-neutral-50 hover:bg-neutral-50 ${
                    n.read ? "" : "bg-amber-50/40"
                  }`}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-neutral-900 leading-snug">
                        {n.title}
                      </p>
                      <p className="text-xs text-neutral-600 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-neutral-400 mt-1">
                        {new Date(n.created_at).toLocaleString("es-MX", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
